import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoalModal = ({ isOpen, onClose, goalType, websiteId, websiteDomain, token, onSuccess, editGoal }) => {
  const [formData, setFormData] = useState({
    title: '',
    pagePath: '',
    eventName: '',
    scrollDepth: 25
  });
  const [availablePaths, setAvailablePaths] = useState([]);
  const [filteredPaths, setFilteredPaths] = useState([]);
  const [showPathDropdown, setShowPathDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pre-populate form when editing
  useEffect(() => {
    if (editGoal && isOpen) {
      if (editGoal.goal_type === 'pageview') {
        setFormData({
          title: editGoal.name,
          pagePath: editGoal.page_path,
          eventName: '',
          scrollDepth: 25
        });
      } else if (editGoal.goal_type === 'custom_event') {
        setFormData({
          title: editGoal.name,
          pagePath: '',
          eventName: editGoal.page_path, // event name stored in page_path
          scrollDepth: 25
        });
      } else if (editGoal.goal_type === 'scroll_depth') {
        const parts = editGoal.page_path.split('|');
        setFormData({
          title: editGoal.name,
          pagePath: parts[0] || '/',
          eventName: '',
          scrollDepth: parseInt(parts[1]) || 25
        });
      }
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        title: '',
        pagePath: '',
        eventName: '',
        scrollDepth: 25
      });
    }
  }, [editGoal, isOpen]);

  useEffect(() => {
    if (isOpen && (goalType === 'pageview' || goalType === 'scroll_depth')) {
      fetchAvailablePaths();
    }
  }, [isOpen, goalType]);

  useEffect(() => {
    if (formData.pagePath) {
      const filtered = availablePaths.filter(path =>
        path.toLowerCase().includes(formData.pagePath.toLowerCase())
      );
      setFilteredPaths(filtered);
    } else {
      setFilteredPaths(availablePaths);
    }
  }, [formData.pagePath, availablePaths]);

  const fetchAvailablePaths = async () => {
    try {
      const res = await axios.get(`${API}/analytics/${websiteId}/available-paths`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailablePaths(res.data.paths || []);
      setFilteredPaths(res.data.paths || []);
    } catch (err) {
      console.error('Failed to fetch paths:', err);
      setAvailablePaths(['/']);
      setFilteredPaths(['/']);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let goalData = {
        goal_type: goalType
      };

      if (goalType === 'pageview') {
        goalData.name = formData.title || `Pageview: ${formData.pagePath}`;
        goalData.page_path = formData.pagePath;
      } else if (goalType === 'custom_event') {
        goalData.name = formData.title || formData.eventName;
        goalData.page_path = formData.eventName; // Store event name in page_path
      } else if (goalType === 'scroll_depth') {
        goalData.name = formData.title || `Scroll ${formData.scrollDepth}%: ${formData.pagePath}`;
        goalData.page_path = `${formData.pagePath}|${formData.scrollDepth}`; // Store path and depth
      }

      if (editGoal) {
        // Update existing goal
        await axios.put(`${API}/goals/${editGoal.id}`, goalData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new goal
        goalData.website_id = websiteId;
        await axios.post(`${API}/goals`, goalData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      onSuccess();
      handleClose();
    } catch (err) {
      console.error(`Failed to ${editGoal ? 'update' : 'create'} goal:`, err);
      alert(`Failed to ${editGoal ? 'update' : 'create'} goal. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      pagePath: '',
      eventName: '',
      scrollDepth: 25
    });
    setShowPathDropdown(false);
    onClose();
  };

  const selectPath = (path) => {
    setFormData({ ...formData, pagePath: path });
    setShowPathDropdown(false);
  };

  const getModalTitle = () => {
    return editGoal ? `Edit goal for ${websiteDomain}` : `Add goal for ${websiteDomain}`;
  };

  const getModalDescription = () => {
    if (goalType === 'pageview') {
      return (
        <>
          Pageview goals allow you to measure how many people visit a specific page or section of your site.{' '}
          <a href="#" className="text-blue-600 hover:underline">Learn more in our docs ↗</a>.
        </>
      );
    } else if (goalType === 'custom_event') {
      return 'Custom events allow you to track specific user interactions like button clicks, form submissions, etc.';
    } else if (goalType === 'scroll_depth') {
      return 'Scroll depth goals track how far down users scroll on a specific page.';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.3 }}
              className="goal-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="goal-modal-header">
                <h2 className="goal-modal-title">{getModalTitle()}</h2>
                <button onClick={handleClose} className="goal-modal-close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Description */}
              <p className="goal-modal-description">{getModalDescription()}</p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="goal-modal-form">
                {/* Pageview Goal Fields */}
                {goalType === 'pageview' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Page path</label>
                      <div className="path-selector-container">
                        <input
                          type="text"
                          value={formData.pagePath}
                          onChange={(e) => {
                            setFormData({ ...formData, pagePath: e.target.value });
                            setShowPathDropdown(true);
                          }}
                          onFocus={() => setShowPathDropdown(true)}
                          placeholder="Select option or search by typing"
                          className="path-selector-input"
                          required
                        />
                        {showPathDropdown && filteredPaths.length > 0 && (
                          <div className="path-dropdown">
                            {filteredPaths.slice(0, 10).map((path, idx) => (
                              <div
                                key={idx}
                                onClick={() => selectPath(path)}
                                className="path-dropdown-item"
                              >
                                {path}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Goal title (optional)</label>
                      <Input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., Visited About Page"
                        className="form-input"
                      />
                    </div>
                  </>
                )}

                {/* Custom Event Fields */}
                {goalType === 'custom_event' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Event name</label>
                      <Input
                        type="text"
                        value={formData.eventName}
                        onChange={(e) => setFormData({ ...formData, eventName: e.target.value })}
                        placeholder="e.g., button_click, form_submit"
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Goal title (optional)</label>
                      <Input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., Newsletter Signup"
                        className="form-input"
                      />
                    </div>
                  </>
                )}

                {/* Scroll Depth Fields */}
                {goalType === 'scroll_depth' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Page path</label>
                      <div className="path-selector-container">
                        <input
                          type="text"
                          value={formData.pagePath}
                          onChange={(e) => {
                            setFormData({ ...formData, pagePath: e.target.value });
                            setShowPathDropdown(true);
                          }}
                          onFocus={() => setShowPathDropdown(true)}
                          placeholder="Select option or search by typing"
                          className="path-selector-input"
                          required
                        />
                        {showPathDropdown && filteredPaths.length > 0 && (
                          <div className="path-dropdown">
                            {filteredPaths.slice(0, 10).map((path, idx) => (
                              <div
                                key={idx}
                                onClick={() => selectPath(path)}
                                className="path-dropdown-item"
                              >
                                {path}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Scroll depth (%)</label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={formData.scrollDepth}
                        onChange={(e) => setFormData({ ...formData, scrollDepth: parseInt(e.target.value) || 0 })}
                        placeholder="e.g., 25, 50, 75"
                        className="form-input"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Goal title (optional)</label>
                      <Input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., Scrolled 50% on Homepage"
                        className="form-input"
                      />
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="goal-modal-submit"
                >
                  {loading ? (editGoal ? 'Updating...' : 'Adding...') : (editGoal ? 'Update goal' : 'Add goal')}
                </Button>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default GoalModal;
